import { useTheme } from '@mui/material';

/**
 * Chart colour tokens.
 *
 * These are NOT the app's five theme presets. A tenant's white-label preset is
 * a brand preference; a categorical series palette is an encoding that has to
 * stay readable for colourblind users in both modes. Deriving series colours
 * from whatever preset is active would silently break that the moment someone
 * picks the orange theme.
 *
 * Both columns are *selected* for their surface, not flipped — dark is the
 * same eight hues re-stepped for a dark background. Validated with the
 * dataviz palette checker:
 *
 *   light  ALL PASS  (worst adjacent CVD ΔE 9.1 protan; normal-vision 19.6)
 *   dark   ALL PASS  (worst adjacent CVD ΔE 8.4 protan; contrast all >= 3:1)
 *
 * Light mode carries a contrast WARN below 3:1 on three slots, which obligates
 * visible labels rather than colour-alone identity. Every chart here direct-
 * labels or legends its series, so that obligation is met — do not add a
 * colour-only chart without re-checking.
 */

/** Fixed order. Assign by index, never cycle, never sort by value. */
const CATEGORICAL_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const CATEGORICAL_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9'];

/**
 * Reserved. Never reused as "series 4" — a user who learns red means critical
 * must not meet it as an arbitrary category. Always paired with an icon or
 * label, never colour alone.
 */
const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const;

export type StatusRole = keyof typeof STATUS;

export interface ChartTokens {
  categorical: string[];
  status: typeof STATUS;
  /** Single hue, light -> dark. For magnitude, never for identity. */
  sequential: string[];
  grid: string;
  axis: string;
  textPrimary: string;
  textSecondary: string;
  surface: string;
  isDark: boolean;
  /** Slot by index, wrapping only as a last resort (see cap below). */
  series: (index: number) => string;
}

export const useChartTokens = (): ChartTokens => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const categorical = isDark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;

  return {
    categorical,
    status: STATUS,
    sequential: isDark
      ? ['#0b2f52', '#14476f', '#1e5f8c', '#2a78d6', '#5b9ae5']
      : ['#dbeafe', '#a8cbf0', '#6fa8e3', '#2a78d6', '#1a4f8f'],
    grid: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
    axis: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.28)',
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    surface: theme.palette.background.paper,
    isDark,
    series: (index: number) => categorical[index % categorical.length],
  };
};

/**
 * Recharts shares these across every chart so axes and grids stay recessive
 * and consistent. Spread into the component, do not restyle per chart.
 */
export const axisProps = (t: ChartTokens) => ({
  tick: { fill: t.textSecondary, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: t.grid },
  stroke: t.axis,
});

export const gridProps = (t: ChartTokens) => ({
  stroke: t.grid,
  strokeDasharray: '0',
  vertical: false,
});

export const tooltipStyle = (t: ChartTokens) => ({
  contentStyle: {
    background: t.surface,
    border: `1px solid ${t.grid}`,
    borderRadius: 8,
    fontSize: 12,
    boxShadow: t.isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(15,23,42,0.12)',
  },
  // Labels wear text tokens, never the series colour.
  labelStyle: { color: t.textPrimary, fontWeight: 600, marginBottom: 4 },
  itemStyle: { color: t.textSecondary },
});

/** Above this many series, fold the tail into "Other" rather than inventing hues. */
export const MAX_SERIES = 7;

/**
 * Per-category accent for widget header chips.
 *
 * These are DECORATIVE identity, not data encoding — they tell you at a glance
 * that a tile belongs to Finance rather than Sales. They are intentionally
 * separate from both the categorical series palette (which encodes data) and
 * the status palette (which is reserved for state), so a green chip on a
 * Procurement tile is never mistaken for a "good" signal.
 */
const CATEGORY_ACCENTS: Record<string, string> = {
  finance: '#6366f1',
  sales: '#8b5cf6',
  procurement: '#10b981',
  inventory: '#f59e0b',
  projects: '#3b82f6',
  hr: '#14b8a6',
  helpdesk: '#06b6d4',
  treasury: '#0284c7',
  approvals: '#f97316',
  activity: '#a855f7',
  notifications: '#ef4444',
  general: '#64748b',
};

export const categoryAccent = (category?: string): string =>
  CATEGORY_ACCENTS[(category || 'general').toLowerCase()] || CATEGORY_ACCENTS.general;
